/**
 * PDP shipping label beside the price.
 *
 * /cart.js line items do not include product metafields, so this does not
 * read the cart JSON. cart/add and cart/change in this theme publish
 * cartUpdate after the cart is saved. We then GET
 * {product.url}?section_id={section.id}. That response is Liquid, which
 * reads cart.items and item.product.metafields.custom.free_shipping.value.
 * Variant changes already replace #price-{section.id} from the same section
 * response, so the label is re-rendered there without a second listener.
 *
 * If that section response is missing or the request fails, fail closed:
 * show Free Shipping only when this product's own metafield was true.
 * Otherwise restore the calculated-at-checkout note. Do not promise free
 * shipping from an unreadable cart.
 */
function shippingNoteSectionUrl(productUrl, sectionId, base) {
  if (!productUrl || !sectionId) return '';
  const url = new URL(productUrl, base);
  url.searchParams.set('section_id', sectionId);
  return url.toString();
}

function failClosedNoteKind(productFreeShipping) {
  return productFreeShipping === 'true' || productFreeShipping === true ? 'free' : 'calculated';
}

if (!customElements.get('product-shipping-note')) {
  customElements.define(
    'product-shipping-note',
    class ProductShippingNote extends HTMLElement {
      connectedCallback() {
        if (this.unsubscribe || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.onCartUpdate = () => this.refreshFromSection();
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
      }

      disconnectedCallback() {
        this.unsubscribe?.();
        this.unsubscribe = null;
        this.abortController?.abort();
      }

      refreshFromSection() {
        const requestUrl = shippingNoteSectionUrl(
          this.dataset.productUrl,
          this.dataset.sectionId,
          window.location.origin
        );
        if (!requestUrl) {
          this.applyFailClosed();
          return Promise.resolve();
        }

        this.abortController?.abort();
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        return fetch(requestUrl, {
          signal,
          cache: 'no-store',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })
          .then((response) => {
            if (!response.ok) throw new Error('shipping-note-section');
            return response.text();
          })
          .then((html) => {
            if (signal.aborted) return;
            const next = new DOMParser().parseFromString(html, 'text/html').querySelector('product-shipping-note');
            const nextLive = next?.querySelector('[data-shipping-note-live]');
            if (!next || !nextLive) throw new Error('shipping-note-missing');
            this.replaceFromSection(next, nextLive);
          })
          .catch((error) => {
            if (error?.name === 'AbortError') return;
            this.applyFailClosed();
          });
      }

      replaceFromSection(next, nextLive) {
        if (next.dataset.productFreeShipping) {
          this.dataset.productFreeShipping = next.dataset.productFreeShipping;
        }
        this.querySelectorAll('template[data-note]').forEach((node) => node.remove());
        const live = this.querySelector('[data-shipping-note-live]');
        next.querySelectorAll('template[data-note]').forEach((node) => {
          this.insertBefore(node, live);
        });
        if (live) live.innerHTML = nextLive.innerHTML;
      }

      applyFailClosed() {
        const live = this.querySelector('[data-shipping-note-live]');
        if (!live) return;
        const kind = failClosedNoteKind(this.dataset.productFreeShipping);
        const template = this.querySelector(`template[data-note="${kind}"]`);
        live.replaceChildren();
        if (template) live.append(template.content.cloneNode(true));
      }
    }
  );
}
